<template>
  <div class="backtest-page">
    <header class="page-heading">
      <div>
        <h4>题材持续性回测</h4>
        <p>基于真·主线持续性强化过滤，动态选拔题材核心龙头，严格遵守 T+1 盘中即时止损与移动止盈。</p>
      </div>
      <div class="version-badges" v-if="result">
        <b-badge variant="primary" class="mr-1">{{ result.engine_version || 'topic_continuity_backtest_v6' }}</b-badge>
        <b-badge :variant="form.mainline_filter ? 'success' : 'secondary'">{{ form.mainline_filter ? '主线过滤已激活' : '全题材模式' }}</b-badge>
      </div>
    </header>

    <b-card class="control-card" no-body>
      <b-form @submit.prevent="runBacktest" class="control-form-wrapper">
        <!-- 核心常规参数 -->
        <div class="control-grid">
          <b-form-group label="开始日期">
            <b-form-input v-model="form.start_date" type="date" required size="sm" />
          </b-form-group>
          <b-form-group label="结束日期">
            <b-form-input v-model="form.end_date" type="date" required size="sm" />
          </b-form-group>
          <b-form-group label="每日题材数" description="弱市聚焦主线第1名">
            <b-form-input v-model.number="form.max_topics" type="number" min="1" max="20" size="sm" />
          </b-form-group>
          <b-form-group label="每题材核心股" description="只上身位龙头">
            <b-form-input v-model.number="form.max_core_stocks" type="number" min="1" max="10" size="sm" />
          </b-form-group>
          <b-form-group label="最低持续分" description="≥75分过滤一日游脉冲">
            <b-form-input v-model.number="form.min_continuity_score" type="number" min="0" max="100" step="1" size="sm" />
          </b-form-group>
          <b-form-group label="单票最大仓位(%)" description="推荐50%弹韧均衡">
            <b-form-input v-model.number="weightPercent" type="number" min="1" max="100" step="1" size="sm" />
          </b-form-group>
          <b-form-group label="转弱确认天数" description="退潮极速平仓">
            <b-form-input v-model.number="form.exit_confirmation_days" type="number" min="1" max="5" size="sm" />
          </b-form-group>
          <div class="run-action">
            <b-button type="submit" variant="primary" size="sm" block :disabled="loading">
              <b-spinner v-if="loading" small class="mr-1" />{{ loading ? '正在回测' : '运行回测' }}
            </b-button>
          </div>
        </div>

        <!-- v6 核心战法与风控高级配置栏 -->
        <div class="strategy-v6-bar">
          <div class="v6-switches">
            <b-form-checkbox v-model="form.mainline_filter" switch size="sm" class="custom-switch-item" title="只出击多日稳居前3名的大主线，弱市垃圾时间坚决100%空仓保本">
              <strong>真·主线过滤器</strong>
              <small class="text-muted d-none d-lg-inline ml-1">(弱市空仓保本，每月只做1~3个大题材)</small>
            </b-form-checkbox>
            <b-form-checkbox v-model="form.intraday_execution" switch size="sm" class="custom-switch-item" title="严格遵守A股T+1，持仓≥1天盘中触及止损或止盈线即刻挂单出局">
              <strong>盘中 T+1 执行</strong>
              <small class="text-muted d-none d-lg-inline ml-1">(盘中止盈止损，规避次日跳空暴亏)</small>
            </b-form-checkbox>
            <b-form-checkbox v-model="form.intraday_entry" switch size="sm" class="custom-switch-item" title="主线确认当日盘中即刻拿底仓，避免次日竞价大幅高开追高接盘">
              <strong>盘中确立建仓</strong>
              <small class="text-muted d-none d-lg-inline ml-1">(突破当日拿底仓，避免次日追高)</small>
            </b-form-checkbox>
          </div>

          <div class="v6-inputs">
            <div class="inline-input-group">
              <label>硬止损:</label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="form.stop_loss_pct" type="number" step="0.5" min="1" max="20" class="num-input" />
              </b-input-group>
            </div>
            <div class="inline-input-group">
              <label>止盈激活:</label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="form.trailing_stop_trigger_pct" type="number" step="0.5" min="1" max="50" class="num-input" />
              </b-input-group>
            </div>
            <div class="inline-input-group">
              <label>回撤止盈:</label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="form.trailing_stop_pct" type="number" step="0.5" min="1" max="20" class="num-input" />
              </b-input-group>
            </div>
            <div class="inline-input-group replay-group">
              <b-form-checkbox v-model="intraday" size="sm" class="mr-1">单日分钟回放</b-form-checkbox>
              <b-form-input v-model="asOfTime" type="time" size="sm" min="09:30" max="15:00" :disabled="!intraday" class="time-input" />
            </div>
          </div>
        </div>
      </b-form>
    </b-card>

    <b-alert v-if="error" show variant="danger" class="mb-3">{{ error }}</b-alert>
    <template v-if="result && result.status === 'ok'">
      <b-alert show :variant="result.data_quality.strict_point_in_time ? 'success' : 'warning'" class="quality-alert">
        <strong>数据可信度：{{ result.data_quality.strict_point_in_time ? '严格历史时点 (严禁未来函数)' : '研究级回测' }}</strong>
        <span v-for="warning in result.data_quality.warnings" :key="warning">{{ warning }}</span>
      </b-alert>

      <!-- 单日分钟回放卡片 -->
      <section class="metrics" v-if="result.mode === 'intraday'">
        <article><small>回放时点</small><b>{{ result.trade_date }} {{ result.as_of_time }}</b></article>
        <article><small>行情覆盖</small><b>{{ result.quote_count }}</b></article>
        <article><small>推荐题材</small><b>{{ result.summary.recommendation_count }}</b></article>
        <article><small>核心股</small><b>{{ result.summary.core_stock_count }}</b></article>
        <article><small>5日样本</small><b>{{ result.summary.five_day_sample_count }}</b></article>
        <article><small>5日均值</small><b :class="numberClass(result.summary.five_day_average_return_pct)">{{ pct(result.summary.five_day_average_return_pct) }}</b></article>
        <article><small>5日胜率</small><b>{{ pct(result.summary.five_day_win_rate) }}</b></article>
      </section>

      <!-- 区间回测资金汇总卡片 -->
      <section class="metrics period-metrics" v-if="result.mode !== 'intraday' && portfolio.summary">
        <article><small>区间总收益率</small><b :class="numberClass(portfolio.summary.total_return_pct)">{{ pct(portfolio.summary.total_return_pct) }}</b></article>
        <article><small>最大回撤</small><b class="down">{{ pct(portfolio.summary.max_drawdown_pct) }}</b></article>
        <article><small>期末账户总权益</small><b>{{ money(portfolio.summary.final_equity) }}</b></article>
        <article><small>平仓胜率</small><b :class="portfolio.summary.closed_trade_win_rate >= 60 ? 'up' : ''">{{ pct(portfolio.summary.closed_trade_win_rate) }}</b></article>
        <article><small>总平仓笔数</small><b>{{ portfolio.summary.sell_count || 0 }} 笔</b></article>
        <article><small>移动止盈</small><b class="up">{{ portfolio.summary.take_profit_count || 0 }} 次</b></article>
        <article><small>盘中止损</small><b class="down">{{ portfolio.summary.stop_loss_count || 0 }} 次</b></article>
        <article><small>相对全题材超额</small><b :class="numberClass(evaluation.five_day_excess_return_pct)">{{ pct(evaluation.five_day_excess_return_pct) }}</b></article>
      </section>

      <!-- 净值轨迹折线 -->
      <section class="curve-card" v-if="result.mode !== 'intraday' && portfolio.equity_curve && portfolio.equity_curve.length">
        <div class="section-title">
          <h5>净值收益曲线</h5>
          <span>区间: {{ result.parameters.start_date }} 至 {{ result.parameters.end_date }} | 初始: {{ money(portfolio.summary.initial_cash || 1000000) }}</span>
        </div>
        <svg viewBox="0 0 1000 190" preserveAspectRatio="none" role="img" aria-label="回测净值曲线">
          <line x1="0" y1="170" x2="1000" y2="170" class="axis" />
          <polyline :points="curvePoints" class="equity-line" fill="none" />
        </svg>
        <div class="curve-axis">
          <span>{{ portfolio.equity_curve[0].trade_date }} (净值 1.00)</span>
          <span>{{ portfolio.equity_curve[portfolio.equity_curve.length - 1].trade_date }} (净值 {{ (portfolio.summary.final_equity / (portfolio.summary.initial_cash || 1000000)).toFixed(2) }})</span>
        </div>
      </section>

      <!-- 题材信号推荐效果 -->
      <section class="result-card" v-if="result.mode !== 'intraday'">
        <div class="section-title"><h5>持续主线信号推荐</h5><span>{{ signals.length }} 条主线标的记录</span></div>
        <div class="table-responsive">
          <table class="result-table">
            <thead><tr><th>日期</th><th>题材名称</th><th>阶段状态</th><th>持续评分</th><th>排位</th><th>核心龙头股</th><th>1日涨幅</th><th>3日涨幅</th><th>5日涨幅</th></tr></thead>
            <tbody>
              <tr v-for="signal in signals" :key="signal.signal_date + '-' + signal.topic_id">
                <td>{{ signal.signal_date }}</td><td><strong>{{ signal.topic_name }}</strong></td>
                <td><b-badge :variant="getStateVariant(signal.state)">{{ signal.state }}</b-badge></td>
                <td>{{ number(signal.continuity_score) }}</td><td>{{ signal.rank ? '#' + signal.rank : '--' }}</td>
                <td><span v-for="stock in signal.core_stocks" :key="stock.code" class="stock-chip" :title="coreReason(stock)">{{ stock.name }} ({{ stock.code }}) <b>{{ number(stock.core_score) }}</b></span><em v-if="!signal.core_stocks.length">未达门槛</em></td>
                <td :class="numberClass(signal.return_1d_pct)">{{ pct(signal.return_1d_pct) }}</td>
                <td :class="numberClass(signal.return_3d_pct)">{{ pct(signal.return_3d_pct) }}</td>
                <td :class="numberClass(signal.return_5d_pct)">{{ pct(signal.return_5d_pct) }}</td>
              </tr>
              <tr v-if="!signals.length"><td colspan="9" class="empty">所选区间没有满足主线条件的题材信号（保持空仓保本防守）</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 逐笔成交记录明细表 -->
      <section class="result-card" v-if="result.mode !== 'intraday'">
        <div class="section-title"><h5>逐笔交易记录</h5><span>严格遵守 T+1 规则，盘中即时硬止损与动态移动止盈保护</span></div>
        <div class="table-responsive">
          <table class="result-table compact">
            <thead><tr><th>成交日期</th><th>信号日期</th><th>操作方向</th><th>股票标的</th><th>成交价格</th><th>成交股数</th><th>单笔盈亏</th><th>触发操作原因</th></tr></thead>
            <tbody>
              <tr v-for="(trade, index) in portfolio.trades" :key="index">
                <td>{{ trade.trade_date }}</td><td>{{ trade.signal_date || '--' }}</td>
                <td><b :class="trade.side === 'BUY' ? 'up' : 'down'">{{ trade.side === 'BUY' ? '买入' : '卖出' }}</b></td>
                <td><strong>{{ trade.name }}</strong> ({{ trade.code }})</td>
                <td>¥{{ Number(trade.price).toFixed(2) }}</td>
                <td>{{ trade.shares }} 股</td>
                <td :class="numberClass(trade.return_pct)"><strong>{{ pct(trade.return_pct) }}</strong></td>
                <td><span class="reason-badge" :class="getReasonClass(trade.reason)">{{ formatTradeReason(trade.reason, trade.side) }}</span></td>
              </tr>
              <tr v-if="!portfolio.trades.length"><td colspan="8" class="empty">暂无成交记录（全周期空仓防守）</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

function isoDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'WonderTopicBacktest',
  data() {
    const end = new Date()
    const start = new Date(end.getTime() - 90 * 86400000)
    return {
      loading: false,
      error: '',
      result: null,
      intraday: false,
      asOfTime: '14:30',
      weightPercent: 50, // 优化为默认 50% 仓位 (避免资金大量闲置)
      form: {
        start_date: isoDate(start),
        end_date: isoDate(end),
        initial_cash: 1000000,
        max_topics: 1,
        max_core_stocks: 1,
        exit_confirmation_days: 2, // 优化为默认 2 天确认
        min_continuity_score: 75,  // 优化为默认 75 分门槛，过滤单日脉冲杂毛
        mainline_filter: true,     // v6: 真·主线过滤器 (每月1-3个大题材，弱市空仓防守)
        intraday_execution: true,  // v6: 严格 T+1 盘中即时止损止盈
        intraday_entry: true,      // v6: 突破当日盘中建仓，规避次日追高
        stop_loss_pct: 6.0,        // v6: 6% 硬止损
        trailing_stop_trigger_pct: 7.0, // v6: 浮盈 7% 激活移动止盈
        trailing_stop_pct: 4.0,    // v6: 回撤 4% 止盈落袋
        rotate_core_stocks: true,
        core_rotation_confirmation_days: 1,
        core_retention_rank: 7,
        hold_core_trend: true,
        core_trend_ma_days: 3,
        core_trend_max_drawdown_pct: 10,
        core_trend_break_confirmation_days: 1,
        max_stock_weight: 0.5,
        commission_rate: 0.0003,
        stamp_duty_rate: 0.0005,
        slippage_bps: 5
      }
    }
  },
  computed: {
    portfolio() { return (this.result && this.result.portfolio) || { summary: {}, equity_curve: [], trades: [] } },
    evaluation() { return (this.result && this.result.recommendation_evaluation && this.result.recommendation_evaluation.summary) || {} },
    signals() { return (this.result && this.result.recommendation_evaluation && this.result.recommendation_evaluation.signals) || [] },
    intradayRows() {
      const recommendations = (this.result && this.result.recommendations) || []
      return recommendations.reduce((rows, topic) => rows.concat((topic.core_stocks || []).map(stock => ({
        ...stock,
        topic_id: topic.topic_id,
        topic_name: topic.topic_name,
        state: topic.state,
        continuity_score: topic.continuity_score,
        rank: topic.rank
      }))), [])
    },
    curvePoints() {
      const values = this.portfolio.equity_curve || []
      if (!values.length) return ''
      const equities = values.map(item => Number(item.equity))
      const min = Math.min(...equities)
      const max = Math.max(...equities)
      const span = max - min || 1
      return equities.map((value, index) => {
        const x = values.length === 1 ? 500 : index / (values.length - 1) * 1000
        const y = 165 - (value - min) / span * 145
        return `${x.toFixed(1)},${y.toFixed(1)}`
      }).join(' ')
    }
  },
  methods: {
    pct(value) { if (value === null || value === undefined) return '--'; const n = Number(value); return `${n > 0 ? '+' : ''}${n.toFixed(2)}%` },
    number(value) { return value === null || value === undefined ? '--' : Number(value).toFixed(1) },
    coreReason(stock) { return (stock && stock.reasons && stock.reasons.length) ? stock.reasons.join(' · ') : '动态核心评分' },
    money(value) { return value === null || value === undefined ? '--' : `¥${Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}` },
    numberClass(value) { return Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : '' },
    getStateVariant(state) {
      if (state === '延续增强') return 'danger'
      if (state === '持续稳定') return 'success'
      if (state === '转弱分歧') return 'warning'
      return 'secondary'
    },
    formatTradeReason(reason, side) {
      if (!reason) return side === 'BUY' ? '盘中主线确立建仓' : '正常平仓'
      if (reason.includes('trailing_take_profit')) return '🎯 移动止盈落袋 (盘中)'
      if (reason.includes('stop_loss')) return '🛡️ 触碰硬止损线 (盘中)'
      if (reason.includes('topic_retreat')) return '⚠️ 题材退潮离场'
      if (reason.includes('core_trend_break')) return '📉 均线破位离场'
      return reason
    },
    getReasonClass(reason) {
      if (!reason) return 'reason-buy'
      if (reason.includes('trailing_take_profit')) return 'reason-profit'
      if (reason.includes('stop_loss')) return 'reason-loss'
      return 'reason-exit'
    },
    async runBacktest() {
      this.loading = true
      this.error = ''
      this.result = null
      try {
        const payload = {
          ...this.form,
          end_date: this.intraday ? this.form.start_date : this.form.end_date,
          as_of_time: this.intraday ? this.asOfTime : null,
          max_stock_weight: Number(this.weightPercent) / 100
        }
        const response = await request_zzquant({ url: '/v3/topic/continuity/backtest', method: 'POST', data: payload, timeout: 180000, noRedirect: true })
        const body = response && response.data
        if (!body || body.code !== 20000) throw new Error((body && body.message) || '回测失败')
        this.result = body.data
        if (this.result.status !== 'ok') this.error = this.result.message || '所选区间没有可用数据'
      } catch (error) {
        const detail = error && error.response && error.response.data && error.response.data.detail
        this.error = detail || error.message || '回测失败，请检查数据连接后重试。'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.backtest-page { padding:12px 10px 30px; color:var(--text-main,#263445); }
.page-heading,.section-title { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; }
.page-heading { margin-bottom:12px; }
.page-heading h4 { margin:0; font-weight:700; color:var(--text-main,#263445); }
.page-heading p { margin:4px 0 0; color:var(--text-muted,#718096); font-size:12px; }
.version-badges { display:flex; align-items:center; }
.control-card,.result-card,.curve-card { margin-bottom:14px; border-color:var(--border-color,#dfe5eb); background:var(--bg-card,#fff); border-radius:8px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
.control-form-wrapper { padding:12px; }
.control-grid { display:grid; grid-template-columns:repeat(7,minmax(105px,1fr)) 110px; gap:8px; align-items:start; }
.control-grid >>> .form-group { margin:0; }
.control-grid >>> label { margin-bottom:2px; color:var(--text-muted,#718096); font-size:11px; font-weight:600; }
.control-grid >>> small.form-text { font-size:9px; margin-top:1px; line-height:1.2; color:var(--text-muted,#8898aa); }
.run-action { padding-top:18px; }

/* v6 战法开关控制区 */
.strategy-v6-bar { margin-top:10px; padding-top:10px; border-top:1px dashed var(--border-color,#e2e8f0); display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:12px; }
.v6-switches { display:flex; flex-wrap:wrap; align-items:center; gap:16px; }
.custom-switch-item { margin:0; cursor:pointer; font-size:12px; }
.v6-inputs { display:flex; flex-wrap:wrap; align-items:center; gap:12px; }
.inline-input-group { display:flex; align-items:center; gap:4px; font-size:11px; color:var(--text-muted,#718096); }
.inline-input-group label { margin:0; font-weight:600; white-space:nowrap; }
.num-input { width:65px!important; text-align:center; padding:2px 4px; font-weight:600; }
.replay-group { padding-left:8px; border-left:1px solid var(--border-color,#e2e8f0); }
.time-input { width:80px!important; padding:2px 4px; }

.quality-alert { display:flex; flex-direction:column; gap:3px; font-size:11px; margin-bottom:12px; border-radius:6px; }
.metrics { display:grid; grid-template-columns:repeat(7,minmax(100px,1fr)); gap:8px; margin-bottom:12px; }
.period-metrics { grid-template-columns:repeat(8,minmax(100px,1fr)); }
.metrics article { padding:10px 12px; border:1px solid var(--border-color,#dfe5eb); border-radius:7px; background:var(--bg-card,#fff); box-shadow:0 1px 2px rgba(0,0,0,0.02); }
.metrics small { display:block; color:var(--text-muted,#718096); font-size:10px; font-weight:500; }
.metrics b { display:block; margin-top:3px; font-size:16px; font-variant-numeric:tabular-nums; }

.section-title { padding:10px 14px; border-bottom:1px solid var(--border-color,#e8edf2); }
.section-title h5 { margin:0; font-size:13px; font-weight:700; color:var(--text-main,#263445); }
.section-title span { color:var(--text-muted,#718096); font-size:11px; }

.curve-card svg { width:100%; height:190px; padding:10px 12px 0; }
.axis { stroke:var(--border-color,#e1e6eb); stroke-width:1; }
.equity-line { stroke:#2b6cb0; stroke-width:2.5; vector-effect:non-scaling-stroke; }
.curve-axis { display:flex; justify-content:space-between; padding:4px 14px 10px; color:var(--text-muted,#718096); font-size:10px; }

.result-table { width:100%; min-width:850px; border-collapse:collapse; font-size:11px; }
.result-table th,.result-table td { padding:8px 10px; border-bottom:1px solid var(--border-color,#edf0f3); text-align:left; white-space:nowrap; }
.result-table th { color:var(--text-muted,#718096); font-weight:600; background:var(--bg-muted,#f6f8fa); }
.result-table.compact { min-width:750px; }
.stock-chip { display:inline-block; margin:1px 4px 1px 0; padding:2px 6px; border-radius:4px; background:var(--bg-muted,#edf2f6); }
.stock-chip b { color:#2f6fa8; font-size:10px; margin-left:3px; }
.result-table em,.empty { color:var(--text-muted,#718096); font-style:normal; }
.empty { text-align:center!important; padding:28px!important; }

.reason-badge { display:inline-block; padding:2px 6px; border-radius:4px; font-size:10px; font-weight:500; }
.reason-profit { background:#e6fffa; color:#234e52; border:1px solid #b2f5ea; }
.reason-loss { background:#fff5f5; color:#742a2a; border:1px solid #fed7d7; }
.reason-buy { background:#ebf8ff; color:#2a4365; border:1px solid #bee3f8; }
.reason-exit { background:#f7fafc; color:#4a5568; border:1px solid #e2e8f0; }

.up { color:#c53030!important; }
.down { color:#276749!important; }

@media(max-width:1200px){
  .control-grid { grid-template-columns:repeat(4,minmax(110px,1fr)); }
  .run-action { grid-column:span 4; padding-top:4px; }
  .metrics { grid-template-columns:repeat(4,minmax(100px,1fr)); }
}
@media(max-width:768px){
  .control-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .run-action { grid-column:span 2; }
  .metrics { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .strategy-v6-bar { flex-direction:column; align-items:flex-start; }
  .replay-group { border-left:none; padding-left:0; }
}
</style>
